import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, Clock, X, MessageSquare, Eye } from 'lucide-react';
import axios from 'axios';

const ProducerNotifications = () => {
  const [rejectedProducts, setRejectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasUnreadFeedback, setHasUnreadFeedback] = useState(false);
  const { API } = useContext(AuthContext);

  useEffect(() => {
    fetchRejectedProducts();
  }, []);

  const fetchRejectedProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/my/rejected`);
      setRejectedProducts(response.data);
      
      // Check if there's unread feedback
      const unread = response.data.some(product => !product.feedback_reviewed);
      setHasUnreadFeedback(unread);
    } catch (error) {
      console.error('Error fetching rejected products:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsReviewed = async (productId) => {
    try {
      await axios.post(`${API}/products/${productId}/mark-reviewed`);
      toast.success('Feedback marcado como revisado');
      fetchRejectedProducts(); // Refresh the list
    } catch (error) {
      console.error('Error marking as reviewed:', error);
      toast.error('Erro ao marcar feedback como revisado');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Data não disponível';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card className="card-feira">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Carregando notificações...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (rejectedProducts.length === 0) {
    return (
      <Card className="card-feira">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-600">
            <CheckCircle className="w-5 h-5" />
            Nenhum produto rejeitado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600">Todos os seus produtos estão aprovados ou em análise. Continue o bom trabalho!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-feira">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-orange-600" />
            Feedback do Administrador
            {hasUnreadFeedback && (
              <Badge className="bg-red-100 text-red-800 animate-pulse">
                Novo
              </Badge>
            )}
          </div>
          <Badge variant="outline" className="text-orange-600">
            {rejectedProducts.length} produto{rejectedProducts.length !== 1 ? 's' : ''} rejeitado{rejectedProducts.length !== 1 ? 's' : ''}
          </Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {rejectedProducts.map((product) => (
          <Alert key={product.id} className={`border-l-4 ${
            product.feedback_reviewed 
              ? 'border-l-gray-400 bg-gray-50' 
              : 'border-l-red-400 bg-red-50'
          }`} data-testid={`rejected-product-${product.id}`}>
            <AlertTriangle className={`h-4 w-4 ${
              product.feedback_reviewed ? 'text-gray-500' : 'text-red-500'
            }`} />
            <AlertDescription>
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 mb-1">
                      {product.name}
                    </h4>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                      <Clock className="w-3 h-3" />
                      <span>Rejeitado em: {formatDate(product.approved_at)}</span>
                    </div>
                  </div>
                  
                  {!product.feedback_reviewed && (
                    <Badge className="bg-red-100 text-red-800">
                      Não lido
                    </Badge>
                  )}
                </div>

                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    <MessageSquare className="w-4 h-4 inline mr-1" />
                    Motivo da rejeição:
                  </p>
                  <p className="text-sm text-gray-800">
                    {product.admin_notes || 'Nenhum motivo específico foi informado.'}
                  </p>
                </div>

                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>💡 Dica:</strong> Revise as informações do produto conforme o feedback e cadastre novamente com as correções necessárias.
                  </p>
                </div>

                {!product.feedback_reviewed && (
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={() => markAsReviewed(product.id)}
                      size="sm"
                      className="bg-green-500 hover:bg-green-600 text-white"
                      data-testid={`mark-reviewed-${product.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Marcar como Lido
                    </Button>
                  </div>
                )}

                {product.feedback_reviewed && (
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Feedback revisado em: {formatDate(product.reviewed_at)}
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        ))}

        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="font-medium text-yellow-800 mb-2">Como proceder:</h4>
          <ol className="text-sm text-yellow-700 space-y-1 list-decimal list-inside">
            <li>Leia atentamente o feedback do administrador</li>
            <li>Corrija as informações conforme orientado</li>
            <li>Cadastre o produto novamente com as melhorias</li>
            <li>Marque o feedback como "lido" após revisar</li>
          </ol>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProducerNotifications;